import { useEffect, useState, type ReactNode } from 'react';
import { request, storedSite, sessionHeaders } from '../../../api';
import { useResource } from '../../../shell/hooks';
import { Card, Money, Moment, Resourceful } from '../../../shell/kit';
import type { IdentityKey, IdentityTranslate } from '../i18n';

/**
 * Карточка визита: хронология «въезд → старт мойки → выезд», автомобиль, кто
 * был за рулём, заказы и снимки — кадр камеры въезда, фото до и после мойки
 * (ТЗ М2.6 «снимки в карточке визита», М3.6).
 */
export interface VisitPhotoView {
  readonly id: string;
  readonly stage: 'entry' | 'before' | 'after';
  readonly source: 'lpr' | 'tablet';
  readonly key: string | null;
  readonly url: string | null;
  readonly takenAt: string;
}

export interface VisitCardData {
  readonly id: string;
  readonly siteName: string;
  readonly enteredAt: string;
  readonly serviceStartedAt: string | null;
  readonly exitedAt: string | null;
  readonly exitSource: string | null;
  readonly entrySeconds: number | null;
  readonly cycleSeconds: number | null;
  readonly customer: {
    readonly id: string;
    readonly displayName: string | null;
    readonly phone: string | null;
    readonly role: string | null;
  } | null;
  readonly vehicle: {
    readonly plate: string;
    readonly make: string | null;
    readonly model: string | null;
    readonly bodyType: string | null;
    readonly sizeClass: string | null;
    readonly color: string | null;
  };
  readonly operator: { readonly displayName: string } | null;
  readonly orders: readonly {
    readonly id: string;
    readonly clientOrderNo: number;
    readonly status: string;
    readonly totalMinor: number;
    readonly items: readonly { readonly name: string; readonly qty: number }[];
  }[];
  readonly photos: readonly VisitPhotoView[];
  readonly recognitions: readonly {
    readonly id: string;
    readonly outcome: string;
    readonly confidence: number | null;
    readonly frameAt: string;
    readonly cameraCode: string | null;
    readonly direction: string | null;
  }[];
}

export function fetchVisit(visitId: string): Promise<VisitCardData> {
  return request<VisitCardData>('GET', `/api/v1/visits/${encodeURIComponent(visitId)}`);
}

/**
 * Снимок с сервера. Админка ходит с Bearer-токеном, а `<img src>` заголовков не
 * шлёт, поэтому файл забирается запросом и показывается как blob-ссылка.
 */
async function photoUrl(visitId: string, photoId: string): Promise<string | null> {
  const headers: Record<string, string> = {};
  Object.assign(headers, sessionHeaders());
  const site = storedSite();
  if (site) headers['x-moika-site'] = site;
  const res = await fetch(
    `/api/v1/visits/${encodeURIComponent(visitId)}/photos/${encodeURIComponent(photoId)}`,
    { headers },
  );
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}

function duration(seconds: number | null): string {
  if (seconds === null) return '—';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes > 0 ? `${minutes} мин ${rest} с` : `${rest} с`;
}

function Photo({ visitId, photo, label }: { visitId: string; photo: VisitPhotoView; label: string }) {
  const [src, setSrc] = useState<string | null>(photo.url);
  useEffect(() => {
    if (photo.url || !photo.key) return undefined;
    let url: string | null = null;
    let alive = true;
    void photoUrl(visitId, photo.id).then((value) => {
      url = value;
      if (alive) setSrc(value);
    });
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [visitId, photo.id, photo.url, photo.key]);

  return (
    <figure className="w-48">
      {src ? (
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt={label} className="h-32 w-48 rounded-lg object-cover" />
        </a>
      ) : (
        <div className="h-32 w-48 rounded-lg bg-surface-sunken" />
      )}
      <figcaption className="mt-1 text-xs text-ink-muted">
        {label} · <Moment iso={photo.takenAt} />
      </figcaption>
    </figure>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-1.5 last:border-0">
      <span className="text-sm text-ink-muted">{label}</span>
      <span className="min-w-0 text-right text-sm text-ink">{children}</span>
    </div>
  );
}

export function VisitCardPanel({
  t,
  visitId,
  onClose,
}: {
  t: IdentityTranslate;
  visitId: string;
  onClose: () => void;
}) {
  const resource = useResource(() => fetchVisit(visitId), [visitId]);
  const key = (value: string): IdentityKey => value as IdentityKey;

  return (
    <Card
      title={t('identity.visit.title')}
      actions={
        <button type="button" className="text-sm text-brand" onClick={onClose}>
          {t('identity.visit.close')}
        </button>
      }
    >
      <Resourceful resource={resource}>
        {(visit) => {
          const vehicle = [
            visit.vehicle.plate,
            [visit.vehicle.make, visit.vehicle.model].filter(Boolean).join(' '),
            visit.vehicle.bodyType,
            visit.vehicle.sizeClass ? `класс ${visit.vehicle.sizeClass.toUpperCase()}` : null,
            visit.vehicle.color,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div className="flex flex-col gap-4">
              <div>
                <Row label={t('identity.visit.entered')}>
                  <Moment iso={visit.enteredAt} /> · {visit.siteName}
                </Row>
                <Row label={t('identity.visit.started')}>
                  {visit.serviceStartedAt ? <Moment iso={visit.serviceStartedAt} /> : t('identity.visit.notStarted')}
                </Row>
                <Row label={t('identity.visit.exited')}>
                  {visit.exitedAt ? (
                    <>
                      <Moment iso={visit.exitedAt} />
                      {visit.exitSource ? ` · ${t(key(`identity.visit.exitSource.${visit.exitSource}`))}` : ''}
                    </>
                  ) : (
                    t('identity.visit.onSite')
                  )}
                </Row>
                <Row label={t('identity.visit.entrySpeed')}>{duration(visit.entrySeconds)}</Row>
                <Row label={t('identity.visit.cycle')}>{duration(visit.cycleSeconds)}</Row>
                <Row label={t('identity.visit.vehicle')}>{vehicle}</Row>
                <Row label={t('identity.visit.driver')}>
                  {visit.customer
                    ? `${visit.customer.displayName ?? visit.customer.phone ?? '—'}${
                        visit.customer.role ? ` · ${t(key(`identity.visit.role.${visit.customer.role}`))}` : ''
                      }`
                    : '—'}
                </Row>
                <Row label={t('identity.visit.operator')}>{visit.operator?.displayName ?? '—'}</Row>
              </div>

              <div>
                <h4 className="mb-2 text-sm font-semibold text-ink">{t('identity.visit.orders')}</h4>
                {visit.orders.length === 0 ? (
                  <p className="text-sm text-ink-muted">{t('identity.visit.noOrders')}</p>
                ) : (
                  visit.orders.map((order) => (
                    <Row key={order.id} label={`№ ${order.clientOrderNo}`}>
                      {order.items.map((i) => (i.qty > 1 ? `${i.name} × ${i.qty}` : i.name)).join(', ')} ·{' '}
                      <Money minor={order.totalMinor} />
                    </Row>
                  ))
                )}
              </div>

              <div>
                <h4 className="mb-2 text-sm font-semibold text-ink">{t('identity.visit.photos')}</h4>
                {visit.photos.length === 0 ? (
                  <p className="text-sm text-ink-muted">{t('identity.visit.noPhotos')}</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {visit.photos.map((photo) => (
                      <Photo
                        key={photo.id}
                        visitId={visit.id}
                        photo={photo}
                        label={t(key(`identity.visit.stage.${photo.stage}`))}
                      />
                    ))}
                  </div>
                )}
              </div>

              {visit.recognitions.length > 0 ? (
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-ink">{t('identity.visit.frames')}</h4>
                  {visit.recognitions.map((r) => (
                    <Row key={r.id} label={r.cameraCode ?? '—'}>
                      <Moment iso={r.frameAt} /> · {r.direction ?? ''} · {r.outcome}
                      {r.confidence !== null ? ` · ${Math.round(r.confidence * 100)}%` : ''}
                    </Row>
                  ))}
                </div>
              ) : null}
            </div>
          );
        }}
      </Resourceful>
    </Card>
  );
}
